/**
 * Client-side helpers for the dishwasher-delivery-app API.
 *
 * In dev with `VITE_API_BASE_URL` empty, requests use the Vite proxy
 * (`/api` → `http://localhost:8080`). In prod the base URL points at the
 * deployed Cloud Run / Express service.
 */

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''   // empty → use Vite proxy in dev

export const apiUrl = (path: string): string => {
  const p = path.startsWith('/') ? path : `/${path}`
  return BASE ? `${BASE}${p}` : p
}

export interface ApiSuccess<T> { success: true;  data: T }
export interface ApiError      { success: false; error: { code: string; message: string } }
export type ApiResponse<T> = ApiSuccess<T> | ApiError

export const apiPost = async <T>(path: string, body: unknown): Promise<T> => {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  })
  const json = await res.json() as ApiResponse<T>
  if (!json.success) throw new Error(`${json.error.code}: ${json.error.message}`)
  return json.data
}

export interface TokenResponse {
  token: string
  expiresAt: number
  sessionId: string
  /** Live model the token is locked to; the SPA must pass this to `client.live.connect({ model })`. */
  model: string
}

export const mintLiveToken = (
  scanType: 'model-label' | 'countertop' | 'undersink',
): Promise<TokenResponse> =>
  apiPost<TokenResponse>('/api/live-token', { scanType })

export type VerifyQuality = 'good' | 'blurry' | 'wrong-frame'

export interface VerifyResponse {
  quality: VerifyQuality
  /** One short customer-facing sentence the live model can read aloud. */
  notes: string
  /** Partial findings the verifier could read with confidence. */
  findings: Record<string, unknown>
}

/**
 * Run a verification pass on a single still frame. Returns the parsed
 * findings + quality flag from Gemini 3.1 Flash. The image must be raw
 * base64 (no `data:` prefix). Throws on transport or upstream errors.
 */
export const verifyCapture = (
  scanType: 'model-label' | 'countertop' | 'undersink',
  imageBase64: string,
  focus?: string,
): Promise<VerifyResponse> =>
  apiPost<VerifyResponse>('/api/verify-capture', {
    scanType,
    image: imageBase64,
    ...(focus !== undefined && { focus }),
  })
